/**
 * App bootstrap — runs once before React renders, GATING first paint.
 *
 * Order mirrors experts-web's bootstrap (the verified, prod-hardened shape):
 *   1. loadAuthConfig()        (build-time VITE_ENTRA_* → resolved AuthConfig)
 *   2. initSentry(config)      (BEFORE MSAL, so the auth flow is observable —
 *                               the 2026-06-09 silent-login-bounce lesson)
 *   3. initMsal(config)        (build PublicClientApplication, process redirect)
 *   4. configureApiWithEntra() (plug the scope-aware resolver into the wrapper)
 *
 * After this resolves, every wrapper-routed api.* call carries a bearer from the
 * one resolver and the shell knows the auth state. main.tsx awaits this and only
 * then renders — so ProtectedRoute / the /auth/callback redirect see a settled
 * MSAL session on first paint (no flash of /signin during redirect completion).
 *
 * PHASE 0: config is build-time env, not a backend fetch (no public /config
 * endpoint exists yet). When the edge + a config endpoint land, swap
 * loadAuthConfig() for the fetch — nothing else changes.
 */
import { configureApiWithEntra } from '@/auth/resolver';
import { loadAuthConfig } from '@/auth/config';
import { initMsal } from '@/auth/msal';
import { initSentry } from '@/observability/sentry';

export async function bootstrap(): Promise<void> {
  const config = loadAuthConfig();
  // Sentry first (it carries the DSN) and BEFORE MSAL, so a silent login bounce
  // is captured instead of vanishing.
  initSentry(config);
  await initMsal(config);
  configureApiWithEntra();
}
